/**
 * Morning Kickoff Card
 *
 * 顯示 Morning Brief 的入口卡片
 * - 點擊後打開 MorningBriefModal 顯示完整簡報
 * - 提供週主題設定入口 (type='weekly')
 *
 * ============================================
 * === 功能移除記錄 ===
 * ============================================
 *
 * [2025-10-16] 移除 handleAdjustSchedule
 * 原因: Adjust 功能暫時移除,待後端 AI 服務完成後再恢復
 *
 * 移除內容:
 * - handleAdjustSchedule(adjustmentText: string) 函數
 * - adjustMorningSchedule API 呼叫
 * - 傳給 Modal 的 onAdjustSchedule prop
 *
 * 未來恢復時需要:
 * 1. 加回 handleAdjustSchedule 函數
 * 2. 函數內部呼叫 adjustMorningSchedule API
 * 3. 成功後呼叫 fetchDailyBrief() 重新載入數據
 * 4. 傳給 MorningBriefModal 的 onAdjustSchedule prop
 *
 * ============================================
 * === 重構記錄 ===
 * ============================================
 *
 * [2025-10-16] 分離數據載入和 UI 控制
 *
 * 問題: 原本 loadDailyBrief() 混合了兩個職責
 * - 打開 Modal (setShowBriefModal(true))
 * - 載入數據 (API 呼叫)
 * 導致調整排程後重新載入時,會誤觸 Modal 狀態重置
 *
 * 解決方案: 拆分為兩個函數
 * - fetchDailyBrief(): 純數據載入,不影響 Modal 狀態
 *   用途: 任何需要重新載入數據的場景 (如調整排程後)
 *
 * - loadDailyBrief(): 打開 Modal + 載入數據
 *   用途: 用戶點擊 "View Brief" 按鈕時
 *
 * 好處:
 * - 符合單一職責原則
 * - 避免狀態同步問題
 * - 函數可在不同場景重用
 */

import React, { useState } from 'react';
import Button from '../common/Button';
import TimeBoxPattern from '../common/TimeBoxPattern';
import {
  Sun,
  AlertCircle
} from '../common/icons';
import { useToast } from '../../contexts/ToastContext';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import { useNotificationPermission } from '../../hooks/useNotificationPermission';
import {
  getMorningBriefCache,
  generateDailyKickoff,
  parseScheduleAdjustment,
  applyScheduleChanges,
  applySuggestion,
  markSuggestionAccepted,
} from '../../services/api/morningKickoff';
import { getCurrentUserId } from '../../services/auth';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { logger } from '../../utils/logger';
import {
  confirmDailyKickoff,
  getAcceptedSuggestionKeys,
  addAcceptedSuggestionKey,
  mergeAcceptedSuggestionKeys,
} from './morningKickoffState';
import { eventBus } from '../../utils/eventBus';
import { MorningBriefModal } from './MorningBriefModal';
import {
  DailyBrief,
  DailyKickoffResponse,
  AdjustmentResultData,
  ScheduleOperation,
  TaskSuggestion
} from '../../types/morningKickoff';
import { TimeBox } from '../../types/timeBox';
import { Event } from '../../types/calendar-event';

interface MorningKickoffCardProps {
  date: string;
  onConfirmed?: () => void; // 當用戶確認開始一天時調用，用於通知父組件隱藏卡片
  timeBoxes?: TimeBox[];
  events?: Event[];
}

type CardState = 'collapsed' | 'error';


export const MorningKickoffCard: React.FC<MorningKickoffCardProps> = ({
  date,
  onConfirmed,
  timeBoxes,
  events
}) => {
  const { showToast } = useToast();
  const { showConfirm } = useConfirmDialog();
  const { fcmToken, isSupported, requestPermission } = useNotificationPermission();
  const userId = getCurrentUserId();
  const isDesktop = useMediaQuery('(min-width: 768px)');

  const [cardState, setCardState] = useState<CardState>('collapsed');
  const [isLoading, setIsLoading] = useState(false);
  const [briefData, setBriefData] = useState<DailyBrief | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showBriefModal, setShowBriefModal] = useState(false);

  // Phase 1.5: Two-Phase Commit state
  const [pendingOperations, setPendingOperations] = useState<ScheduleOperation[]>([]);

  // 已接受的 suggestion keys（Modal 開關不影響此 state）
  const [acceptedSuggestionKeys, setAcceptedSuggestionKeys] = useState<string[]>(() =>
    userId ? getAcceptedSuggestionKeys(userId, date) : []
  );

  // 生成基於時間的問候語
  const getTimeBasedGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Day!';
    if (hour < 18) return 'Good Day!';
    return 'Good Day!';
  };

  /**
   * 核心數據載入函數
   *
   * 職責: 純數據載入,不影響 UI 狀態
   * - 呼叫 generateDailyKickoff API
   * - 解析返回的簡報數據
   * - 更新 briefData 狀態
   * - 處理錯誤情況
   *
   * 使用場景:
   * - 初次打開 Modal 時 (由 loadDailyBrief 呼叫)
   * - 調整排程後重新載入 (未來 handleAdjustSchedule 完成後呼叫)
   * - 任何需要刷新簡報數據的場景
   */
  const fetchDailyBrief = async () => {
    if (!userId) return;

    setIsLoading(true);
    setErrorMessage('');

    try {
      // Cache-first：HIT 時繞過 Cloud Function
      const cached = await getMorningBriefCache(userId, date);
      if (cached) {
        logger.dev('MorningKickoff', 'Cache HIT，直接顯示');
        setBriefData({
          greeting: getTimeBasedGreeting(),
          narrative: cached.narrative,
          taskSuggestions: cached.taskSuggestions,
          suggestionOverallReason: cached.suggestionOverallReason,
        });
        if (cached.acceptedSuggestionKeys.length > 0) {
          mergeAcceptedSuggestionKeys(userId, date, cached.acceptedSuggestionKeys);
        }
        setAcceptedSuggestionKeys(getAcceptedSuggestionKeys(userId, date));
        return;
      }

      // Cache MISS / Stale → fallback 呼叫 Cloud Function
      logger.dev('MorningKickoff', 'Cache MISS，呼叫 Cloud Function');
      const result: DailyKickoffResponse = await generateDailyKickoff(userId, date);

      // 解析後端返回的資料結構
      if (result.success && result.data) {
        const data = result.data;

        setBriefData({
          greeting: getTimeBasedGreeting(),
          narrative: data.narrative || '',
          taskSuggestions: data.taskSuggestions,
          suggestionOverallReason: data.suggestionOverallReason,
        });

        // 將後端回傳的 acceptedSuggestionKeys merge 進 localStorage（跨裝置同步）
        if (data.acceptedSuggestionKeys && data.acceptedSuggestionKeys.length > 0) {
          mergeAcceptedSuggestionKeys(userId, date, data.acceptedSuggestionKeys);
        }
        setAcceptedSuggestionKeys(getAcceptedSuggestionKeys(userId, date));

      } else {
        throw new Error(result.error || 'Failed to generate daily kickoff');
      }
    } catch (error) {
      logger.error('MorningKickoff', '載入每日簡報失敗', { error });

      const errorMsg = error instanceof Error ? error.message : '載入失敗';

      // 特殊處理：沒有排程的情況
      if (errorMsg.includes('No schedule found')) {
        setErrorMessage('No schedule found for today. Create some time boxes or tasks to get started!');
      } else {
        setErrorMessage(errorMsg);
      }

      setCardState('error');
      showToast({ title: 'Failed to load', type: 'error' });
      setShowBriefModal(false); // 載入失敗時關閉 Modal
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 打開 Modal 並載入簡報
   *
   * 職責: UI 流程控制
   * - 打開 Modal
   * - 觸發數據載入
   *
   * 使用場景:
   * - 用戶點擊 "View Brief" 按鈕
   */
  const loadDailyBrief = async () => {
    setShowBriefModal(true);
    await fetchDailyBrief();
  };

  /**
   * Phase 1.5: 處理排程調整 - 解析用戶指令（Parse Only）
   *
   * 流程:
   * 1. 調用 parseScheduleAdjustment API（只解析，不寫入 Firestore）
   * 2. Backend 解析 AI + 檢查衝突
   * 3. 保存結構化操作到 pendingOperations
   * 4. 返回預覽數據給 Modal
   *
   * ✅ 修正: Preview 是真正的只讀預覽
   * - Firestore 尚未被修改
   * - Cancel 可以安全取消
   */
  const handleAdjustSchedule = async (adjustmentText: string): Promise<AdjustmentResultData | null> => {
    if (!userId || !adjustmentText.trim()) return null;

    try {
      // ✅ 調用 Parse API（不寫入 Firestore）
      const result = await parseScheduleAdjustment(userId, date, adjustmentText);

      if (result.success && result.data) {
        logger.dev('MorningKickoff', 'Schedule adjustment parsed (no write)', {
          operationsCount: result.data.operations.length,
          hasConflicts: result.data.operations.some(op => op.conflicts && op.conflicts.length > 0)
        });

        // ✅ 保存結構化操作（供 Apply 階段使用）
        setPendingOperations(result.data.operations);

        // 轉換為 AdjustmentResultData 格式（供 Preview 顯示）
        return {
          changes: result.data.operations.map(op => ({
            type: op.type,
            itemId: op.itemId,
            itemTitle: op.itemTitle,
            originalTime: op.originalTime,
            newTime: op.newTime,
            success: op.success,
            conflicts: op.conflicts,
            error: op.error || undefined
          })),
          warnings: result.data.warnings,
          explanation: result.data.explanation
        };
      } else {
        logger.error('MorningKickoff', 'Schedule adjustment parsing failed', { error: result.error });
        showToast({ title: result.error || 'Failed to parse adjustment', type: 'error' });
        return null;
      }
    } catch (error) {
      logger.error('MorningKickoff', '解析排程調整失敗', { error });
      showToast({ title: 'Failed to parse adjustment', type: 'error' });
      return null;
    }
  };

  /**
   * Phase 1.5: 套用變更（Execute Only）
   *
   * ✅ 修正: 這裡才真正寫入 Firestore
   * - 接收保存的 pendingOperations
   * - 調用 applyScheduleChanges API
   * - 執行實際的 Firestore 寫入
   */
  const handleApplyAdjustment = async (): Promise<void> => {
    if (!userId || pendingOperations.length === 0) {
      logger.warn('MorningKickoff', 'No pending operations to apply');
      return;
    }

    try {
      // ✅ 調用 Apply API（實際寫入 Firestore）
      const result = await applyScheduleChanges(userId, date, pendingOperations);

      if (result.success && result.data) {
        logger.info('MorningKickoff', 'Schedule changes applied', {
          applied: result.data.applied,
          failed: result.data.failed
        });

        if (result.data.failed > 0) {
          showToast({
            title: `Applied ${result.data.applied} changes, ${result.data.failed} failed`,
            type: 'warning'
          });
        } else {
          showToast({ title: 'Schedule updated successfully', type: 'success' });
        }

        // 清空待執行操作
        setPendingOperations([]);

        // 🔄 觸發全域排程刷新事件，讓 Today 頁面重新載入 timeBoxes/events
        eventBus.emit('scheduleChanged');
      } else {
        throw new Error(result.error || 'Failed to apply changes');
      }
    } catch (error) {
      logger.error('MorningKickoff', '套用排程變更失敗', { error });
      showToast({ title: 'Failed to apply changes', type: 'error' });
      throw error;
    }
  };

  /**
   * 接受 AI 建議：呼叫 API，成功後前進到下一張
   */
  const handleSuggestionAccept = async (suggestion: TaskSuggestion): Promise<void> => {
    if (!userId) return;
    const result = await applySuggestion(userId, date, suggestion);
    if (result.success) {
      const key = suggestion.taskId ?? suggestion.itemId ?? suggestion.taskName;
      // 1. 立即寫入 localStorage
      addAcceptedSuggestionKey(userId, date, key);
      setAcceptedSuggestionKeys(getAcceptedSuggestionKeys(userId, date));
      // 2. Fire-and-forget 同步到後端
      markSuggestionAccepted(date, key).catch(err =>
        logger.error('MorningKickoff', '同步 acceptedSuggestionKey 至後端失敗', err)
      );
      eventBus.emit('scheduleChanged');
    } else {
      throw new Error(result.error || 'Failed to apply suggestion');
    }
  };

  /**
   * 重新載入 Brief（不影響 Modal 開關狀態）
   */
  const handleReloadBrief = async (): Promise<void> => {
    await fetchDailyBrief();
  };

  // 確認開始一天
  const handleConfirmStartDay = async () => {
    // 標記為已確認
    await confirmDailyKickoff(userId, date);
    setShowBriefModal(false);
    setCardState('collapsed');
    showToast({ title: 'Have a great day!', type: 'success' });

    // 通知父組件隱藏卡片
    onConfirmed?.();

    // 檢查是否需要詢問推播授權
    if (!fcmToken && isSupported) {
      const shouldEnable = await showConfirm({
        title: 'Stay on Track with Reminders',
        message: 'Get gentle reminders for your morning brief and day close',
        confirmText: 'Enable Notifications',
        cancelText: 'Not Now',
      });

      if (shouldEnable) {
        await requestPermission();
      }
    }
  };

  // 顯示 Daily Kickoff 的配置
  const getCardConfig = () => {
    return {
      icon: Sun,
      title: 'Morning Brief Ready',
      subtitle: briefData?.greeting || 'Ready to start your day?',
      buttonText: 'View Brief'
    };
  };

  const config = getCardConfig();

  // ========== 錯誤狀態 ==========
  if (cardState === 'error') {
    return (
      <div className="relative overflow-hidden rounded-xl md:rounded-1.5xl bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50 border-2 border-red-200">
        <TimeBoxPattern category="routine" opacity={0.04} />

        <div className="relative z-content p-5 md:p-6">
          <div className="flex items-start gap-3 md:gap-4">
            <div className="flex items-center justify-center w-12 h-12 md:w-14 md:h-14 rounded-full bg-red-100 text-red-600 shrink-0">
              <AlertCircle size={isDesktop ? 28 : 24} strokeWidth={2} />
            </div>
            <div className="flex-1">
              <div className="text-lg md:text-xl font-semibold text-gray-900 mb-2">
                Unable to Load Brief
              </div>
              <p className="text-sm md:text-base text-gray-700 leading-relaxed mb-4">
                {errorMessage}
              </p>
              <div className="flex gap-3">
                <Button
                  onClick={() => setCardState('collapsed')}
                  variant="secondary"
                  size="sm"
                >
                  Close
                </Button>
                <Button
                  onClick={loadDailyBrief}
                  variant="primary"
                  size="sm"
                >
                  Retry
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ========== 正常狀態 - Collapsed Card ==========
  if (cardState === 'collapsed') {
    const CardIcon = config.icon;

    return (
      <>
        <div className="morning-kickoff-card">
          <div className={`flex ${isDesktop ? 'items-center justify-between' : 'flex-col'} relative z-content p-4`}>
            <div className={`flex items-center ${isDesktop ? '' : 'w-full mb-3'}`}>
              <div className="sun-icon-container mr-3 flex-shrink-0">
                <CardIcon
                  size={isDesktop ? 36 : 28}
                  className="text-orange-600 opacity-90"
                  strokeWidth={2}
                />
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">{config.title}</h3>
                <p className="text-sm text-gray-700">
                  {config.subtitle}
                </p>
              </div>
            </div>

            <button
              onClick={loadDailyBrief}
              disabled={isLoading}
              className={`${isDesktop ? 'ml-4' : 'w-full mt-6'} px-4 py-2 rounded-full border border-orange-400 bg-orange-500 bg-opacity-10 hover:bg-opacity-20 text-orange-900 font-medium shadow-sm hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {isLoading ? 'Loading...' : config.buttonText}
            </button>
          </div>
        </div>

        {/* Morning Brief Modal - Phase 1: 完整的智慧調整功能 */}
        <MorningBriefModal
          isOpen={showBriefModal}
          onClose={() => setShowBriefModal(false)}
          briefData={briefData}
          isLoading={isLoading}
          onConfirmStartDay={handleConfirmStartDay}
          onAdjustSchedule={handleAdjustSchedule}
          onApplyAdjustment={handleApplyAdjustment}
          onReloadBrief={handleReloadBrief}
          timeBoxes={timeBoxes}
          events={events}
          onSuggestionAccept={handleSuggestionAccept}
          acceptedSuggestionKeys={acceptedSuggestionKeys}
        />
      </>
    );
  }

  // 不應到達這裡，所有狀態都已處理
  return null;
};
